import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import FeaturedCollections from "@/components/FeaturedCollections";
import { ArrowRight, Sparkles, Heart, Users, ShieldCheck } from "lucide-react";

const CATEGORY_FALLBACK = {
  "Candles": "https://images.pexels.com/photos/9518738/pexels-photo-9518738.jpeg?auto=compress&cs=tinysrgb&w=900",
  "Bath & Body": "https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?auto=format&fit=crop&w=900&q=80",
  "Skincare": "https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=900&q=80",
  "Jewelry": "https://images.unsplash.com/photo-1611652022419-a9419f74343d?auto=format&fit=crop&w=900&q=80",
  "Metaphysical & Mystical": "https://images.unsplash.com/photo-1632980205460-e490e885e848?auto=format&fit=crop&w=900&q=80",
  "Gift Boxes": "https://images.unsplash.com/photo-1612619731964-3cb3c33c6c29?auto=format&fit=crop&w=900&q=80",
};

// Buyer-facing labels mapped to the real product categories used for links.
const CATEGORY_TILES = [
  { label: "Candles", cat: "Candles" },
  { label: "Bath & Body", cat: "Bath & Body" },
  { label: "Skincare", cat: "Skincare" },
  { label: "Jewelry", cat: "Jewelry" },
  { label: "Crystals & Spiritual", cat: "Metaphysical & Mystical" },
  { label: "Gifts", cat: "Gift Boxes" },
];

const TRUST = [
  [Sparkles, "Handmade Products"],
  [Users, "Independent Makers"],
  [Heart, "Thoughtfully Curated"],
  [ShieldCheck, "Secure Checkout"],
];

export default function Landing() {
  const [homepage, setHomepage] = useState({ new_arrivals: [], best_sellers: [], featured: [] });
  const [collections, setCollections] = useState([]);
  useEffect(() => {
    api.get("/products/homepage").then(({ data }) => setHomepage(data)).catch(() => {});
    api.get("/collections").then(({ data }) => setCollections(data)).catch(() => {});
  }, []);

  const newArrivals = homepage.new_arrivals || [];
  const bestSellers = homepage.best_sellers || [];
  const featured = homepage.featured || [];

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20 pb-14 lg:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7 fade-in-up">
            <div className="text-xs tracking-[0.3em] uppercase text-primary mb-6 font-semibold">
              ◦ A Curated Handmade Marketplace
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-[4.25rem] leading-[1.05] tracking-tight font-medium">
              Discover Handmade Beauty, <br className="hidden sm:block" />
              <em className="text-primary">Self-Care &amp; Meaningful Gifts</em>
            </h1>
            <p className="mt-7 max-w-xl text-base lg:text-lg leading-relaxed text-muted-foreground">
              Shop distinctive products created by independent makers. From hand-poured candles and
              small-batch self-care to jewelry, crystals and thoughtful gifts, discover something made
              with intention.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/#new-arrivals"
                data-testid="hero-shop-btn"
                className="btn-primary px-7 py-4 text-sm rounded-sm inline-flex items-center gap-2"
              >
                Shop New Arrivals <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/#categories" data-testid="hero-categories-btn" className="btn-outline px-7 py-4 text-sm rounded-sm">
                Explore Categories
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 fade-in">
            <div className="aspect-[3/4] overflow-hidden rounded-sm grain relative">
              <img
                src="https://images.pexels.com/photos/4202325/pexels-photo-4202325.jpeg?auto=compress&cs=tinysrgb&w=900"
                alt="Handmade candle by an independent maker"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-background/95 backdrop-blur-sm p-5 rounded-sm border border-border">
                <div className="text-[10px] tracking-[0.2em] uppercase text-primary font-semibold mb-1">◦ Featured Maker</div>
                <div className="font-serif text-xl">Maison Lumi</div>
                <div className="text-xs text-muted-foreground mt-1">Hand-poured candles · Montréal</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-border bg-secondary/40" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-6">
          {TRUST.map(([Icon, label]) => (
            <div key={label} className="flex items-center gap-2.5 justify-center md:justify-start">
              <Icon className="w-4 h-4 text-primary flex-shrink-0" strokeWidth={1.75} />
              <span className="text-[11px] sm:text-xs tracking-[0.12em] uppercase font-semibold">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* SHOP BY CATEGORY */}
      <section id="categories" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 scroll-mt-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-3">◦ Shop by Category</div>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight">Find your kind of handmade.</h2>
          </div>
          <Link to="/shop" className="text-xs tracking-[0.2em] uppercase hover:text-primary transition-colors font-semibold hidden sm:block">Shop all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6">
          {CATEGORY_TILES.map(({ label, cat }) => (
            <Link
              key={label}
              to={`/shop?category=${encodeURIComponent(cat)}`}
              data-testid={`category-tile-${cat}`}
              className="group relative block overflow-hidden rounded-sm bg-secondary aspect-[4/3] sm:aspect-[16/10]"
            >
              <img
                src={CATEGORY_FALLBACK[cat]}
                alt={label}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-[1.05]"
                style={{ transition: "transform 0.6s ease" }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-5">
                <h3 className="font-serif text-lg lg:text-2xl text-background tracking-tight">{label}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* NEW ARRIVALS */}
      {newArrivals.length > 0 && (
        <section id="new-arrivals" className="max-w-7xl mx-auto px-6 lg:px-10 pb-16 lg:pb-24 scroll-mt-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-3">◦ New Arrivals</div>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tight">Just in from the studios.</h2>
            </div>
            <Link to="/shop" className="text-xs tracking-[0.2em] uppercase hover:text-primary transition-colors font-semibold hidden sm:block">View all →</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {newArrivals.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* BEST SELLERS */}
      {bestSellers.length > 0 && (
        <section id="best-sellers" className="max-w-7xl mx-auto px-6 lg:px-10 pb-16 lg:pb-24 scroll-mt-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-3">◦ Best Sellers</div>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tight">Most loved this week.</h2>
            </div>
            <Link to="/shop" className="text-xs tracking-[0.2em] uppercase hover:text-primary transition-colors font-semibold hidden sm:block">View all →</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {bestSellers.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* FEATURED PRODUCTS */}
      {featured.length > 0 && (
        <section id="featured-products" className="max-w-7xl mx-auto px-6 lg:px-10 pb-16 lg:pb-24 scroll-mt-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-3">◦ Featured Products</div>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tight">Handpicked for you.</h2>
            </div>
            <Link to="/shop" className="text-xs tracking-[0.2em] uppercase hover:text-primary transition-colors font-semibold hidden sm:block">View all →</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Featured collections (existing) */}
      <FeaturedCollections collections={collections} />

      {/* BUYER SECTION */}
      <section className="bg-secondary/50 py-16 lg:py-24" data-testid="buyer-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-4">◦ Why Alteza</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
            Find Something You Won&rsquo;t <br className="hidden sm:block" />
            <em className="text-primary">Find Everywhere.</em>
          </h2>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            Alteza brings together independent makers creating distinctive products in small batches.
            Discover meaningful gifts, everyday self-care and beautiful products made with creativity and care.
          </p>
          <Link to="/shop" data-testid="buyer-start-shopping" className="btn-primary inline-flex items-center gap-2 mt-9 px-7 py-4 text-sm rounded-sm">
            Start Shopping <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Seller Spotlight (editorial maker story) */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-12 items-center">
        <div className="md:col-span-5">
          <img
            src="https://images.pexels.com/photos/3737599/pexels-photo-3737599.jpeg?auto=compress&cs=tinysrgb&w=900"
            alt="Maker Léa at her workshop"
            className="w-full aspect-[4/5] object-cover rounded-sm grain"
          />
        </div>
        <div className="md:col-span-6 md:col-start-7">
          <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-4">◦ Maker Spotlight</div>
          <h2 className="font-serif text-3xl lg:text-5xl tracking-tight leading-tight">
            &ldquo;I built Maison Lumi at my kitchen table.<br />
            <em className="text-primary">Alteza let it become a brand.&rdquo;</em>
          </h2>
          <p className="mt-6 text-muted-foreground leading-relaxed max-w-md">
            Léa pours every candle by hand in Montréal. In her first three months on Alteza, she
            shipped to twelve countries — each order a little piece of her studio.
          </p>
          <div className="mt-8 flex items-center gap-6 text-sm">
            <div><span className="font-serif text-2xl block">218</span><span className="text-xs text-muted-foreground tracking-wider uppercase">Orders shipped</span></div>
            <div><span className="font-serif text-2xl block">4.9★</span><span className="text-xs text-muted-foreground tracking-wider uppercase">Avg. rating</span></div>
            <div><span className="font-serif text-2xl block">12</span><span className="text-xs text-muted-foreground tracking-wider uppercase">Countries</span></div>
          </div>
        </div>
      </section>

      {/* About / How Alteza Works */}
      <section id="how-it-works" className="bg-foreground text-background py-16 lg:py-24 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-4">◦ About Alteza</div>
          <h2 className="font-serif text-3xl lg:text-5xl tracking-tight max-w-2xl">A simple, premium home for handmade.</h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              [Sparkles, "Discover", "Browse curated handmade collections — every brand is reviewed by us."],
              [Heart, "Shop with confidence", "Direct support to independent, small-batch makers. Real stories, real craft."],
              [Users, "Made with intention", "Small batches, thoughtful materials, and products you won't find on every shelf."],
            ].map(([Icon, h, b], i) => (
              <div key={h}>
                <Icon className="w-7 h-7 text-primary mb-5" strokeWidth={1.5} />
                <div className="text-xs tracking-[0.2em] uppercase text-background/50 font-semibold mb-2">Step {i + 1}</div>
                <h3 className="font-serif text-3xl">{h}</h3>
                <p className="mt-4 text-sm text-background/70 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
          <div className="mt-14">
            <Link to="/shop" className="btn-primary px-7 py-3.5 text-sm rounded-sm inline-flex items-center gap-2">Start shopping <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* Seller recruitment (secondary, near footer) */}
      <section className="bg-secondary/50 border-t border-border py-12 lg:py-14" data-testid="seller-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-semibold mb-2">◦ For Makers</div>
            <h3 className="font-serif text-2xl lg:text-3xl tracking-tight">Are You a Handmade Business?</h3>
            <p className="mt-2 text-sm text-muted-foreground max-w-xl leading-relaxed">
              Join a growing handmade marketplace designed to help independent makers reach new customers.
            </p>
          </div>
          <Link to="/sell" data-testid="seller-cta-btn" className="btn-outline whitespace-nowrap px-6 py-3 text-sm rounded-sm">
            Sell on Alteza
          </Link>
        </div>
      </section>
    </div>
  );
}
